/**
 * 模組頁面代碼 : 1420-UI-01
 * 頁面說明 : 滿意度管理資料清單列表頁面
 * @module modules/satisfaction-management/components/SatisfactionManagementPage
 */

import React, {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {BaseComponent} from 'web/components/BaseComponent';
import enumList from 'web/utils/enumList';
import moment from 'moment';
import {
  CASE_PRIORITY_HIGH,
  CASE_PRIORITY_NORMAL,
  FLG_CLOSE,
  FLG_OPEN,
  FLG_SEND,
} from 'web/constant';
import {showMessage} from 'web/utils/modalHelper';
import {useSatisfactionManagementPage} from '../hooks/useSatisfactionManagementPage';

import 'react-bootstrap-typeahead/css/Typeahead.css';
import {
  getPrevSearchParams,
  saveSearchParams,
  clearSearchParams,
} from 'web/redux/slices/SatisfactionSlice';

const SatisfactionManagementPage = (props) => {
  const baseComponent = new BaseComponent(props);
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const dispatch = useDispatch();
  const today = moment();
  const prevSearchParams = useSelector(getPrevSearchParams);

  const {
    surveyList,
    setSurveyList,
    getSurveyList,
    searchCasePriority,
    setSearchCasePriority,
    searchStartDate,
    setSearchStartDate,
    searchEndDate,
    setSearchEndDate,
    searchFlgStatus,
    setSearchFlgStatus,
  } = useSatisfactionManagementPage(showMessage, baseComponent);

  const handleSearch = (e) => {
    e.preventDefault();
    // 記下當前的 params，當進入detals回來時能取得原先資料
    dispatch(
      saveSearchParams({
        casePriority: searchCasePriority,
        flgStatus: searchFlgStatus,
        startTime: searchStartDate,
        endTime: searchEndDate,
      }),
    );
    getSurveyList(
      searchCasePriority,
      searchFlgStatus,
      searchStartDate,
      searchEndDate,
    ); // 可改傳參數：searchCasePriority, searchFlgStatus...
  };

  useEffect(() => {
    baseComponent.checkPermission(
      enumList.moduleEnums.case,
      enumList.accessModeEnums.read,
    );
    if (Object.values(prevSearchParams).some((v) => v !== null)) {
      getSurveyList(
        prevSearchParams.casePriority,
        prevSearchParams.flgStatus,
        prevSearchParams.startTime,
        prevSearchParams.endTime,
      );
      setSearchCasePriority(prevSearchParams.casePriority);
      setSearchFlgStatus(prevSearchParams.flgStatus);
      setSearchStartDate(prevSearchParams.startTime);
      setSearchEndDate(prevSearchParams.endTime);
    } else {
      getSurveyList(searchCasePriority, searchFlgStatus, today, today); // 初始載入
    }

    return () => {
      if (!location.pathname.includes('satisfaction-management')) {
        dispatch(clearSearchParams());
      }
    };
  }, []);

  // useEffect(() => {
  // const handleClick = (event) => {
  //   const button = event.target.closest('.btn');
  //   if (button && button.id.startsWith('survey-detail-') && surveyList.length > 0) {
  //     const index = parseInt(button.id.split('-')[2]);
  //     const caseNumber = surveyList[index].caseNumber;
  //     navigate(`/module/satisfaction-survey/${caseNumber}`);
  //   }
  // };
  // if (tableRef.current) {
  //   tableRef.current.addEventListener('click', handleClick);
  // }
  // return () => {
  //   if (tableRef.current) {
  //     tableRef.current.removeEventListener('click', handleClick);
  //   }
  // };
  // }, [surveyList]);

  return (
    <section id="datatable-container">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <svg
              className="nav__icon nav__icon--breadcrumb me-3"
              aria-hidden="true"
            >
              <use href="#ico-home-line"></use>
            </svg>
            服務調查
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            滿意度管理
          </li>
        </ol>
      </nav>
      <h4>滿意度管理</h4>
      <div className="datatable__search">
        <form
          className="d-flex gap-2 align-items-end needs-validation"
          noValidate
          onSubmit={handleSearch}
        >
          <div className="flex-fill w-100">
            <label htmlFor="searchCasePriority" className="form-label">
              等級
            </label>
            <select
              className="form-select form-select-sm"
              id="searchCasePriority"
              value={searchCasePriority}
              onChange={(e) => setSearchCasePriority(e.target.value)}
            >
              <option value="0">全部</option>
              <option value={enumList.casePriority.high}>
                {CASE_PRIORITY_HIGH}
              </option>
              <option value={enumList.casePriority.normal}>
                {CASE_PRIORITY_NORMAL}
              </option>
            </select>
          </div>
          <div className="flex-fill w-100">
            <label htmlFor="searchFlgStatus" className="form-label">
              狀態
            </label>
            <select
              className="form-select form-select-sm"
              id="searchFlgStatus"
              value={searchFlgStatus}
              onChange={(e) => setSearchFlgStatus(e.target.value)}
            >
              <option value="0">全部</option>
              <option value={enumList.satisfactionFlgStatus.close}>
                {FLG_CLOSE}
              </option>
              <option value={enumList.satisfactionFlgStatus.open}>
                {FLG_OPEN}
              </option>
              <option value={enumList.satisfactionFlgStatus.send}>
                {FLG_SEND}
              </option>
            </select>
          </div>
          <div className="flex-fill w-100">
            <label htmlFor="searchStartDate" className="form-label">
              開始日期
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              id="searchStartDate"
              value={searchStartDate}
              onChange={(e) => setSearchStartDate(e.target.value)}
            />
          </div>
          <div className="flex-fill w-100">
            <label htmlFor="searchEndDate" className="form-label">
              結束日期
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              id="searchEndDate"
              value={searchEndDate}
              onChange={(e) => setSearchEndDate(e.target.value)}
            />
          </div>
          <div>
            <button className="btn btn-primary" type="submit">
              <svg className="btn__icon" aria-hidden="true">
                <use href="#ico-search"></use>
              </svg>
            </button>
          </div>
        </form>
      </div>
      <div className="datatable__data" ref={tableRef}>
        <table
          id="datatable__satisfactionManagement"
          className="display table table-hover align-middle nowrap"
        ></table>
      </div>
    </section>
  );
};

export default SatisfactionManagementPage;
